<template>
  <div
    class="refonte-item-file"
    v-bind:class="props.class">
    <div class="item-file-content-left">
      <div class="item-file-content-title">
        <div class="item-file-content-title-text">
          <div
            class="item-file-content-title-text-top"
            v-bind:title="props.title">
            {{ props.title }}
          </div>
          <div
            v-if="props.subtitle"
            class="item-file-content-title-text-bottom">
            {{ props.subtitle }}
          </div>
        </div>
      </div>
    </div>
    <div class="item-file-content-middle">
      <div
        class="item-file-content-status"
        v-if="!props.no_signature_needed">
        <div
          v-bind:class="`item-file-content-status-text ${props.file_signed ? 'text-jinius-green' : 'text-refonte-secondary'}`">
          <div class="item-file-content-status-text-icon">
            <IconValidate20Green
              class="text-jinius-green"
              v-if="props.file_signed" />
            <IconTime
              class="text-refonte-secondary"
              v-else />
          </div>
          <span>{{
            props.file_signed ? props.signed_label : props.to_sign_label
          }}</span>
        </div>
      </div>
    </div>
    <div class="item-file-content-right">
      <refonte-button
        v-if="!props.no_signature_needed && props.file_signed_editable"
        class="item-file-content-upload"
        variant="secondary"
        v-bind:label_center="props.label_center"
        v-bind:icon_left="IconUpload"
        subtext="la version signée"
        v-on:click="openFileDialog">
        {{ props.file_signed ? 'Modifier' : 'Téléverser' }}
        <input
          v-bind:id="props.id"
          type="file"
          ref="input"
          v-on:change="handleUpload"
          hidden />
      </refonte-button>
      <refonte-button
        v-if="props.on_click_download"
        v-bind:label_center="props.label_center"
        class="item-file-content-download"
        variant="secondary"
        v-bind:icon_left="IconDownload"
        v-bind:subtext="
          !props.no_signature_needed
            ? `la version ${props.file_signed ? '' : 'non '}${props.version_label}`
            : undefined
        "
        v-on:click="props.on_click_download">
        Télécharger
      </refonte-button>
    </div>
  </div>
  <refonte-modal-info
    v-bind:show="infoModal"
    v-bind:on_close="closeInfoModal"
    v-bind:content="`Le fichier est trop volumineux (${uploadMaxSize}MB maximum).`" />
</template>

<script setup lang="ts">
  import IconUpload from '@/assets/img/refonte/icons/display/icon-upload.svg?component';
  import IconDownload from '@/assets/img/refonte/icons/display/icon-download.svg?component';
  import RefonteButton from '@/components/refonte/buttons/refonte-button.vue';
  import IconTime from '@/assets/img/refonte/icons/information/icon-time.svg?component';
  import IconValidate20Green from '@/assets/img/refonte/icons/information/icon-validate-20-green.svg?component';
  import { ref, onMounted } from 'vue';
  import { API } from '@/http-common';
  import RefonteModalInfo from '@/components/refonte/modals/refonte-modal-info.vue';
  const props = withDefaults(
    defineProps<{
      id: string;
      title: string;
      subtitle?: string;
      on_click_download?: Function;
      on_click_upload_refresh?: Function;
      no_download_text?: string;
      signed_label?: string;
      to_sign_label?: string;
      file_signed?: boolean;
      file_signed_editable?: boolean;
      no_signature_needed?: boolean;
      class?: string;
      upload_url?: string;
      label_center?: boolean;
      version_label?: string;
    }>(),
    {
      no_download_text: '-',
      signed_label: 'Signée',
      to_sign_label: 'En attente de signature',
      file_signed: false,
      file_signed_editable: true,
      no_signature_needed: false,
      class: '',
      version_label: 'signée'
    }
  );
  const input = ref(null);
  //2MB
  const uploadMaxSize = 2;
  const infoModal = ref<boolean>(false);
  const openInfoModal = () => {
    infoModal.value = true;
  };
  const closeInfoModal = () => {
    infoModal.value = false;
  };
  onMounted(() => {
    if (input.value) {
      input.value.focus();
    }
  });

  const openFileDialog = () => {
    if (input.value) {
      input.value.click();
    }
  };

  const handleUpload = async (event) => {
    if (props.upload_url) {
      const file = event.target.files[0];
      const formData = new FormData();

      formData.append('file', file);

      if (file.size > uploadMaxSize * 1024 * 1024) {
        openInfoModal();
        return;
      }
      try {
        await API.post(props.upload_url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'SKIP-REDIRECTION': true
          }
        });
        if (props.on_click_upload_refresh) {
          await props.on_click_upload_refresh();
        }
      } catch (e) {
        //On ne fait rien;
      }
    }
  };
</script>

<style lang="scss" scoped>
  .refonte-item-file {
    @apply flex flex-grow items-center bg-refonte-pale-violet-2;
    gap: 4px;
    border-radius: 8px;
    padding: 6px 6px 6px 12px;
    max-width: 700px;
    width: 100%;
    min-height: 48px;

    .item-file-content-left {
      @apply flex flex-grow items-center justify-end;
      gap: 4px;
      width: 60%;
      max-width: 280px;

      .item-file-content-title {
        @apply flex flex-grow items-start;
        gap: 2px;

        .item-file-content-title-text {
          @apply flex flex-grow flex-col items-start;
          gap: 2px;

          .item-file-content-title-text-top {
            @apply self-stretch overflow-hidden text-ellipsis text-refonte-black;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 1;
            font-size: 13px;
            font-weight: 400;
            line-height: 16px;
          }
          .item-file-content-title-text-bottom {
            @apply self-stretch overflow-hidden text-ellipsis text-refonte-black-40;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 1;
            font-size: 12px;
            font-weight: 400;
            line-height: 14px;
          }
        }
      }
      .item-file-content-no-download {
        @apply flex items-center justify-center whitespace-nowrap text-refonte_blue-600;
        padding: 3px;
        min-width: 24px;
        font-size: 13px;
        line-height: 16px;
      }
      .item-file-content-download-separator {
        @apply shrink-0;
      }
      .item-file-content-space {
        @apply shrink-0;
        width: 2px;
      }
    }
    .item-file-content-middle {
      @apply flex flex-grow items-center;
      gap: 4px;
      width: 60%;
      max-width: 280px;

      .item-file-content-status {
        @apply flex flex-grow items-center;
        gap: 4px;

        .item-file-content-status-text {
          @apply flex flex-grow items-center;
          gap: 2px;
          margin-right: 6px;

          .item-file-content-status-text-icon {
            @apply flex shrink-0 items-center justify-center;
            width: 20px;
            height: 20px;

            :deep(svg) {
              width: 18px;
              height: 18px;
            }
          }

          span {
            @apply whitespace-nowrap;
            font-size: 13px;
            line-height: 16px;
            font-weight: 400;
          }
        }
      }
    }
    .item-file-content-right {
      @apply flex flex-grow flex-row-reverse items-center;
      gap: 4px;
      width: 100%;
      .item-file-content-download {
        width: 50%;
        min-width: 125px;
      }
      .item-file-content-upload {
        width: 50%;
        min-width: 105px;
      }
    }

    @media (max-width: 515.98px) {
      @apply flex-col items-stretch;

      .item-file-content-left {
        max-width: 100%;

        .item-file-content-download-separator {
          @apply hidden;
        }
        .item-file-content-space {
          @apply hidden;
        }
      }
    }
  }
</style>
